import { CardData, Connection } from '../types';
import { calculateNodeMetrics, convertLtToMinutes } from './calculations';

/**
 * Parâmetros globais para cálculo de métricas
 */
export interface GlobalParams {
  /** Demanda global do cliente (ex: 100 pçs/dia) */
  globalDemand: number;
  /** Tempo disponível em minutos (ex: 480 min/dia) */
  workTime: number;
  /** Takt Time calculado: workTime / globalDemand (min/pç) */
  taktTime: number;
}

/**
 * Métricas do dashboard calculadas pela Engine Lean
 */
export interface DashboardMetrics {
  /** Custo total mensal (R$) - Soma de (FTEs do nó * Custo/min * Tempo) */
  totalCost: number;
  /** Total de FTE (soma dos FTEs de todos os nós) */
  totalFTE: number;
  /** ID do nó que é o gargalo (maior TP) */
  bottleneckId: string | null;
  /** Nome do nó gargalo */
  bottleneckName: string | null;
  /** Lead Time do caminho crítico (soma dos TPs do caminho mais longo) */
  leadTime: number;
  /** Eficiência (PCE) - Percentual de tempo de valor agregado */
  efficiency: number;
}

/**
 * FASE 3.3: Engine de Cálculo Lean
 * 
 * Calcula métricas agregadas do projeto baseadas nos nós do canvas.
 * Esta engine alimenta o Widget Lateral e o Dashboard.
 * 
 * @param nodes - Array de nós (cards) do canvas
 * @param edges - Array de conexões (edges) entre nós (opcional, inferido de conexoes_saida)
 * @param globalParams - Parâmetros globais (demanda, tempo disponível, takt time)
 * @returns Métricas calculadas do dashboard
 */
export function calculateProjectMetrics(
  nodes: CardData[],
  edges: Connection[] | null,
  globalParams: GlobalParams
): DashboardMetrics {
  // Filtrar apenas nós ativos e não anotações
  const activeNodes = nodes.filter(
    node => node.status === 'ativo' && 
    node.tipo !== 'anotacao' && 
    node.tipo !== 'card_inicial' && 
    node.tipo !== 'card_final'
  );

  // Se não houver nós ativos, retornar métricas zeradas
  if (activeNodes.length === 0) {
    return {
      totalCost: 0,
      totalFTE: 0,
      bottleneckId: null,
      bottleneckName: null,
      leadTime: 0,
      efficiency: 0,
    };
  }

  // 1. CALCULAR CUSTO TOTAL E FTE TOTAL
  let totalCost = 0;
  let totalFTE = 0;
  let totalTimeVA = 0; // Tempo de Valor Agregado (para PCE)
  let totalTime = 0; // Tempo total (TP + TRA)

  // Mapa para armazenar métricas calculadas por nó (evitar recalcular)
  const nodeMetricsMap = new Map<string, ReturnType<typeof calculateNodeMetrics>>();

  activeNodes.forEach(node => {
    // Calcular métricas do nó
    const metrics = calculateNodeMetrics(node);
    nodeMetricsMap.set(node.id, metrics);

    // Acumular custo mensal
    totalCost += metrics.monthlyCost || 0;

    // Acumular FTE teórico
    totalFTE += metrics.fteTheoretical || 0;

    // Calcular tempos para PCE - Usar conversão segura (Fix Gemini)
    const ltValue = Number(node.lt) || 0;
    const tpInMinutes = convertLtToMinutes(ltValue, node.tpUnit);
    const tra = Number(node.tt) || 0;
    const totalNodeTime = tpInMinutes + tra;
    totalTime += totalNodeTime;

    // Se for atividade de valor agregado, acumular tempo VA
    if (node.tipo_atividade === 'VA') {
      totalTimeVA += tpInMinutes;
    }
  });

  // 2. IDENTIFICAR GARGALO (nó com maior TRA - Cycle Time)
  let maxTRA = 0;
  let bottleneckId: string | null = null;
  let bottleneckName: string | null = null;

  activeNodes.forEach(node => {
    const traInMinutes = Number(node.tt) || 0; // TRA (Tempo de Realização)
    
    if (traInMinutes > maxTRA) {
      maxTRA = traInMinutes;
      bottleneckId = node.id;
      bottleneckName = node.atividade || node.nome_fluxo || 'Nó sem nome';
    }
  });

  // 3. CALCULAR LEAD TIME (Caminho Crítico usando DFS)
  // Encontrar o caminho mais longo do grafo (critical path)
  let leadTime = 0;

  // Criar mapa de nós para navegação rápida
  const nodeMap = new Map<string, CardData>();
  nodes.forEach(n => {
    if (n.status === 'ativo') nodeMap.set(n.id, n);
  });

  // Encontrar nó inicial
  const startNode = nodes.find(n => n.tipo === 'card_inicial' && n.status === 'ativo');
  
  if (startNode) {
    // DFS para encontrar todos os caminhos e o mais longo
    const completedPaths: number[] = [];
    
    const traverse = (currentId: string, currentSum: number, visited: string[]): void => {
      if (visited.includes(currentId)) return; // Evitar loops
      
      const node = nodeMap.get(currentId);
      if (!node) return;
      
      const newVisited = [...visited, currentId];
      
      // Calcular tempo do nó - Usar conversão segura (Fix Gemini)
      let nodeTime = 0;
      if (node.tipo !== 'card_inicial') {
        const ltValue = Number(node.lt) || 0;
        nodeTime = convertLtToMinutes(ltValue, node.tpUnit);
      }
      
      const newSum = currentSum + nodeTime;
      
      // Se não tem saídas, é fim do caminho
      if (!node.conexoes_saida || node.conexoes_saida.length === 0) {
        completedPaths.push(newSum);
        return;
      }
      
      // Continuar para próximos nós
      node.conexoes_saida.forEach(conn => {
        traverse(conn.targetId, newSum, newVisited);
      });
    };
    
    traverse(startNode.id, 0, []);
    
    // Lead Time = maior caminho encontrado
    if (completedPaths.length > 0) {
      leadTime = Math.max(...completedPaths);
    }
  } else {
    // Fallback: somar todos os TPs se não houver nó inicial
    activeNodes.forEach(node => {
      const ltValue = Number(node.lt) || 0;
      const tpInMinutes = convertLtToMinutes(ltValue, node.tpUnit);
      leadTime += tpInMinutes;
    });
  }

  // 4. CALCULAR EFICI`NCIA (PCE - Process Cycle Efficiency)
  // PCE = (Tempo de Valor Agregado / Tempo Total) * 100
  let efficiency = 0;
  if (totalTime > 0) {
    efficiency = (totalTimeVA / totalTime) * 100;
    // Garantir que seja um número válido
    if (!isFinite(efficiency) || isNaN(efficiency)) {
      efficiency = 0;
    }
  }

  return {
    totalCost: Math.round(totalCost * 100) / 100, // Arredondar para 2 casas decimais
    totalFTE: Math.round(totalFTE * 100) / 100, // Arredondar para 2 casas decimais
    bottleneckId,
    bottleneckName,
    leadTime: Math.round(leadTime * 100) / 100, // Arredondar para 2 casas decimais
    efficiency: Math.round(efficiency * 100) / 100, // Arredondar para 2 casas decimais
  };
}


